import { useRef, useState, useCallback, useEffect, useMemo, type MouseEvent } from "react";
import type {
  Diagnostic,
  EdgeDirection,
  NodeMetadata,
  DomainEdgeDetail,
  NodeDiffMeta,
  CategoryId,
} from "@karasu-tools/core";
import { parseNodePathRefId } from "@karasu-tools/core";
import { NodeDetailPanel } from "./NodeDetailPanel.js";
import { EdgeDetailPanel, type SingleEdgeDetail } from "./EdgeDetailPanel.js";
import { EdgeContextMenu } from "./EdgeContextMenu.js";
import { FocusCanvas } from "./focus-canvas/FocusCanvas.js";
import { canFocus, readFocusSource, relationsCount, type Focus } from "./focus-canvas/build.js";
import { useCommand } from "../keyboard/use-command.js";
import { attachNodeFocus, type NodeFocusOptions } from "./focus-canvas/node-focus.js";
import { useFormattedDiagnostic } from "../i18n/format-diagnostic.js";
import { useTranslation } from "../i18n/index.js";
import { diagnosticLocationLabel, findingKeys } from "../utils/diagnostic-location.js";
import type { HighlightAttribute } from "../state/app-reducer.js";
import { useRevealNode } from "../state/preview-reveal.js";

interface PreviewPaneProps {
  svg: string;
  diagnostics: Diagnostic[];
  viewPath?: string[];
  nodeMetadata: Map<string, NodeMetadata>;
  /**
   * Metadata keyed by a card's `data-node-path` (#2917). A click reads the
   * card's path first, so two cards sharing a bare `data-node-id` (`Shop.Api`
   * and `Admin.Api` on the multi-system root) each open their own metadata and
   * drill into their own system; `nodeMetadata` (bare id) is the fallback for
   * cards that carry no path.
   */
  nodeMetadataByPath?: Map<string, NodeMetadata>;
  onDrillDown?: (newPath: string[]) => void;
  /**
   * Called when user clicks a deploy container to cross-navigate to system
   * view. Hands over the node the container realizes (`data-realized-node-id`,
   * a bare node id the system view can match), or `null` when the container
   * carries none — a qualified or narrowed container (#2818, ADR-2714) still
   * switches views, but nothing there answers to it, so nothing is highlighted.
   */
  onContainerClick?: (realizedNodeId: string | null) => void;
  /** Called when user clicks the deploy button on a system node to cross-navigate to deploy view */
  onDeployButtonClick?: (serviceId: string) => void;
  /** Called when user clicks the team label on a system node to cross-navigate to org view */
  onTeamButtonClick?: (teamId: string) => void;
  /** Called when user clicks a category ⊖/⊕ control to collapse/expand it (#1821). */
  onCategoryToggle?: (category: CategoryId) => void;
  onGroupToggle?: (groupId: string) => void;
  /** Called when the user clicks a ⊕/⊖ in-place expansion control on a service (#1921). */
  onExpandToggle?: (serviceId: string) => void;
  /** Called when user clicks an owned service link on an org team node to cross-navigate to system view */
  onOwnedServiceClick?: (serviceId: string) => void;
  /**
   * Node id to highlight after cross-navigation. Always a node id: every
   * producer (deploy button, detail panel, container click, outline, hash)
   * hands one over, and `highlightAttribute` says which element of this pane
   * stands for a node.
   */
  highlightedNodeId?: string | null;
  /**
   * The one attribute this pane matches `highlightedNodeId` against (#2818),
   * chosen by whoever handed the id over: a deploy jump names
   * `data-realized-node-id` (the node a container realizes), the outline and
   * the node views name `data-node-id`. One attribute per highlight, not a
   * fallback chain: the deploy view's `data-node-id` is the unit id space,
   * and an unclassified unit spelled like a node would otherwise take a
   * highlight meant for the container (TPL-2818).
   */
  highlightAttribute?: HighlightAttribute;
  /** Called when a node interaction or a click on the diagram background clears the cross-navigation highlight */
  onClearHighlight?: () => void;
  /**
   * Jumps to the node's definition: the detail panel's "Jump to editor", and a
   * Cmd/Ctrl+click on the node (ADR-177, #3311). Unset, neither is offered.
   */
  onJumpToEditor?: (nodeId: string) => void;
  /**
   * Per-node diff metadata from `compileSystemDiff`. When provided, the
   * detail panel surfaces the before/after annotation list as +/- lines
   * (Issue #738 / design doc D-2).
   */
  nodeDiff?: Map<string, NodeDiffMeta>;
  /**
   * The same keyed by each card's `data-node-path` (#3126). The detail panel
   * reads it first, the way it reads `nodeMetadataByPath`, so a card that shares
   * its id with a card in another system frame shows its own annotation diff.
   */
  nodeDiffByPath?: Map<string, NodeDiffMeta>;
  /**
   * Resolved path of the `.krs.style` file the GUI writer should append
   * to. `undefined` disables the right-click → Direction menu's
   * write path (the menu still shows but the items are disabled with a
   * hint).
   */
  styleTargetPath?: string;
  /**
   * Called when the user picks a direction from the edge context menu. The
   * caller is responsible for the actual file write; this prop only
   * forwards intent.
   */
  onPickEdgeDirection?: (canonicalId: string, direction: EdgeDirection) => void;
  /**
   * The host cannot write files yet (the VS Code webview before #3313), so the
   * edge context menu, whose every item is a write, does not open at all.
   */
  readOnly?: boolean;
  /**
   * The open document and the directory other files are shown relative to, so
   * the diagnostic banner can tell a position in the open document from one in
   * an imported file (#2715). Required: a second pane added without them (the
   * entity view's, #2800) would otherwise compile and quietly show full paths.
   */
  currentFilePath: string | null;
  displayRoot: string | null;
  /**
   * Called when the user clicks a team card in the org Tree View, to expand or
   * collapse its members (#2799). Its own prop rather than a reuse of
   * `onGroupToggle`: the org tree's expansion is a different state (which
   * teams show their member grid) from the system view's collapsed group
   * frames, and the two are never on screen at the same time.
   */
  onTeamToggle?: (teamId: string) => void;
  /**
   * Extra class on the pane root, for a sub-mode that swaps the drawn diagram
   * but keeps this pane (the entity view's `preview-pane--entity`, #2800).
   * The base `preview-pane` class is always applied.
   */
  className?: string;
  /**
   * Which diagram is on screen (the view, the drill path, the deploy block).
   * When it changes the zoom and pan go back to the start: a scale and offset
   * chosen for one diagram leave the next one tiny or off screen (#3178). An
   * edit re-renders the same diagram under the same key, so it keeps them.
   */
  diagramKey?: string;
}

interface EdgeContextMenuState {
  x: number;
  y: number;
  canonicalId: string;
  displayLabel: string;
  edgeLabel?: string;
}

type DetailPanelState =
  | { kind: "node"; nodeId: string; nodePath: string | null; anchorX: number; anchorY: number }
  | { kind: "edge"; domainEdges: DomainEdgeDetail[]; anchorX: number; anchorY: number }
  | { kind: "single-edge"; edge: SingleEdgeDetail; anchorX: number; anchorY: number };

const CLICK_THRESHOLD = 3;

// Stable identity for the default: an inline `[]` is a fresh array on every
// render, which breaks referential equality for memoized consumers.
const EMPTY_VIEW_PATH: string[] = [];
const EMPTY_METADATA_BY_PATH: Map<string, NodeMetadata> = new Map();
const INITIAL_TRANSFORM = { scale: 1, x: 0, y: 0 };

export function PreviewPane({
  svg,
  diagnostics,
  viewPath = EMPTY_VIEW_PATH,
  nodeMetadata,
  nodeMetadataByPath = EMPTY_METADATA_BY_PATH,
  onDrillDown,
  onContainerClick,
  onDeployButtonClick,
  onTeamButtonClick,
  onCategoryToggle,
  onGroupToggle,
  onExpandToggle,
  onOwnedServiceClick,
  highlightedNodeId,
  highlightAttribute = "data-node-id",
  onClearHighlight,
  onJumpToEditor,
  nodeDiff,
  nodeDiffByPath,
  styleTargetPath,
  onPickEdgeDirection,
  readOnly,
  currentFilePath,
  displayRoot,
  onTeamToggle,
  className,
  diagramKey,
}: PreviewPaneProps) {
  const formatDiagnostic = useFormattedDiagnostic();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState(INITIAL_TRANSFORM);
  // Reset during render, from the value that changed (as for the focus canvas
  // below), so the new diagram never paints once at the old transform.
  const [transformDiagram, setTransformDiagram] = useState(diagramKey);
  if (transformDiagram !== diagramKey) {
    setTransformDiagram(diagramKey);
    setTransform(INITIAL_TRANSFORM);
  }
  useRevealNode(containerRef, svgRef, setTransform, svg, diagramKey);
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const [detailPanel, setDetailPanel] = useState<DetailPanelState | null>(null);
  const [edgeMenu, setEdgeMenu] = useState<EdgeContextMenuState | null>(null);
  const dragStart = useRef({ x: 0, y: 0 });
  const mouseDownPos = useRef({ x: 0, y: 0 });
  // The focus canvas (#3031): what it has shown, oldest first. Empty when closed.
  const [focusTrail, setFocusTrail] = useState<Focus[]>([]);
  // Closing a canvas the command opened returns focus to the Outline entry
  // the reader selected: the palette input that had focus at open is gone.
  const [returnToOutline, setReturnToOutline] = useState(false);
  const focusOpen = focusTrail.length > 0;
  // Parsed only while the focus canvas is open, and again for each new diagram
  // so the canvas follows an edit.
  const focusSource = useMemo(() => (focusOpen ? readFocusSource(svg) : null), [focusOpen, svg]);
  const focusTop = focusTrail[focusTrail.length - 1];
  const focusShown =
    focusSource !== null && focusTop !== undefined && canFocus(focusSource, focusTop);

  /** Opens the focus canvas on `focus` when the diagram on screen can draw it. */
  const openFocus = useCallback(
    (focus: Focus): boolean => {
      if (!canFocus(readFocusSource(svg), focus)) return false;
      setDetailPanel(null);
      setFocusTrail([focus]);
      return true;
    },
    [svg],
  );

  const handleContextMenu = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (readOnly) return;
      const target = e.target as Element;
      const edgeGroup = target.closest("[data-edge-canonical-id]");
      if (!edgeGroup) return;
      const canonicalId = edgeGroup.getAttribute("data-edge-canonical-id");
      const from = edgeGroup.getAttribute("data-edge-from") ?? "";
      const to = edgeGroup.getAttribute("data-edge-to") ?? "";
      const kind = edgeGroup.getAttribute("data-edge-kind");
      if (!canonicalId) return;
      e.preventDefault();
      const arrow = kind === "async" ? "→→" : "→";
      setEdgeMenu({
        x: e.clientX,
        y: e.clientY,
        canonicalId,
        displayLabel: `${from} ${arrow} ${to}`,
        edgeLabel: edgeGroup.getAttribute("data-edge-label") ?? undefined,
      });
    },
    [readOnly],
  );

  const handlePickDirection = useCallback(
    (direction: EdgeDirection) => {
      if (!edgeMenu) return;
      onPickEdgeDirection?.(edgeMenu.canonicalId, direction);
    },
    [edgeMenu, onPickEdgeDirection],
  );

  // An edit that removes what the focus canvas shows closes it, rather than
  // leaving it on a node or edge the diagram no longer has (and reopening it
  // should a later edit bring that back). Reset during render, from the value
  // that changed, rather than in an effect a render later.
  if (focusOpen && !focusShown) {
    setFocusTrail([]);
    setReturnToOutline(false);
  }

  // The keyboard route to a node's focus canvas (#3057): the node highlighted
  // in the preview, chosen from the Outline view, which the keyboard reaches.
  // Palette-only, no keybinding. A node with no card on the level on screen
  // (the Outline lists every level), or a view that highlights by another
  // attribute than `data-node-id` (deploy, org), opens nothing.
  useCommand({
    id: "view.showRelations",
    title: "Show Relations of Highlighted Node",
    run: () => {
      if (highlightedNodeId && highlightAttribute === "data-node-id") {
        if (openFocus({ kind: "node", id: highlightedNodeId })) setReturnToOutline(true);
      }
    },
  });

  // Node focus and the Relations pill (#3031). Attached once; the options
  // read the latest callbacks through a ref so a re-render never re-attaches.
  const nodeFocusRef = useRef<NodeFocusOptions | null>(null);
  useEffect(() => {
    nodeFocusRef.current = {
      relationsLabel: (count) => t("focusCanvas.relations", { count }),
      onRelations: (id) => {
        openFocus({ kind: "node", id });
      },
      isSuspended: () => focusOpen,
    };
  });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    return attachNodeFocus(el, {
      relationsLabel: (count) => nodeFocusRef.current!.relationsLabel(count),
      onRelations: (id) => nodeFocusRef.current!.onRelations(id),
      isSuspended: () => nodeFocusRef.current!.isSuspended(),
    });
  }, []);

  // Radix Popover handles outside-click + Escape dismissal internally via
  // its DismissableLayer (see EdgeContextMenu — Popover migration, #1368).
  // The bespoke document listeners are no longer needed.

  const visibleDiagnostics = diagnostics.filter(
    (d) => d.severity === "error" || d.severity === "warning" || d.severity === "info",
  );
  const hasErrors = diagnostics.some((d) => d.severity === "error");
  const diagnosticMessages = visibleDiagnostics.map((d) => formatDiagnostic(d));
  const diagnosticKeys = findingKeys(visibleDiagnostics, diagnosticMessages);
  const diagnosticBannerItems = visibleDiagnostics.map((d, i) => ({
    d,
    message: diagnosticMessages[i],
    location: diagnosticLocationLabel(d.loc, { currentFilePath, displayRoot }, (line) =>
      t("preview.location.line", { line }),
    ),
    key: diagnosticKeys[i],
  }));

  // Attach the zoom handler as a native, non-passive wheel listener. React's
  // synthetic onWheel is registered passively (React 17+), so a preventDefault
  // there is a no-op and the page/ancestor scrolls while zooming (#1537).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: globalThis.WheelEvent) => {
      // Subtrees that own their scrolling (e.g. the detail panels) opt out via
      // data-wheel-zoom-ignore. We can't rely on their React synthetic
      // stopPropagation here because this is a native listener on the ancestor
      // container, which fires first during the bubble phase (#1537).
      if ((e.target as Element | null)?.closest?.("[data-wheel-zoom-ignore]")) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setTransform((prev) => ({
        ...prev,
        scale: Math.max(0.1, Math.min(5, prev.scale * delta)),
      }));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (e.button !== 0) return;
      isDraggingRef.current = true;
      setIsDragging(true);
      dragStart.current = {
        x: e.clientX - transform.x,
        y: e.clientY - transform.y,
      };
      mouseDownPos.current = { x: e.clientX, y: e.clientY };
    },
    [transform.x, transform.y],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      setTransform((prev) => ({
        ...prev,
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y,
      }));
    },
    [isDragging],
  );

  const calcAnchor = useCallback((target: Element) => {
    const rect = target.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return null;

    // Position panel to the right of the target
    let anchorX = rect.right - containerRect.left + 8;
    const anchorY = rect.top - containerRect.top;

    // If near right edge, position to the left
    if (anchorX + 360 > containerRect.width) {
      anchorX = rect.left - containerRect.left - 368;
      if (anchorX < 0) anchorX = 8;
    }

    return { anchorX, anchorY };
  }, []);

  const openDetailPanel = useCallback(
    (nodeId: string, target: Element) => {
      const anchor = calcAnchor(target);
      if (!anchor) return;
      // The card's own path, when the canvas emits one (#2917).
      const nodePath = target.getAttribute("data-node-path");
      setDetailPanel({ kind: "node", nodeId, nodePath, ...anchor });
    },
    [calcAnchor],
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      const wasDragging = isDraggingRef.current;
      isDraggingRef.current = false;
      setIsDragging(false);

      if (!wasDragging) return;

      // Only trigger actions if mouse didn't move (click, not drag)
      const dx = Math.abs(e.clientX - mouseDownPos.current.x);
      const dy = Math.abs(e.clientY - mouseDownPos.current.y);
      if (dx > CLICK_THRESHOLD || dy > CLICK_THRESHOLD) return;

      const target = e.target as Element;

      // Check for domain-edge detail click (implicit aggregated edges)
      const edgeGroup = target.closest("[data-domain-edges]");
      if (edgeGroup) {
        const raw = edgeGroup.getAttribute("data-domain-edges");
        if (raw) {
          try {
            const domainEdges = JSON.parse(raw) as DomainEdgeDetail[];
            const anchor = calcAnchor(edgeGroup);
            if (anchor) {
              setDetailPanel({ kind: "edge", domainEdges, ...anchor });
            }
          } catch {
            // malformed JSON — ignore
          }
        }
        return;
      }

      // A regular edge that carries an edge property block (#2543). Keyed on
      // the payload attributes rather than `data-edge-canonical-id`, for two
      // reasons: only an edge with something to show should open a panel (an
      // empty one would fire on every edge in the diagram), and an edge whose
      // canonical id was cleared by a base collision still has prose to show.
      // Right-click still opens EdgeContextMenu (see handleContextMenu).
      const detailEdge = target.closest("[data-edge-description], [data-edge-links]");
      if (detailEdge) {
        const anchor = calcAnchor(detailEdge);
        if (anchor) {
          const rawLinks = detailEdge.getAttribute("data-edge-links");
          let links: SingleEdgeDetail["links"] = [];
          if (rawLinks) {
            try {
              links = JSON.parse(rawLinks) as SingleEdgeDetail["links"];
            } catch {
              // malformed JSON: show the rest of the edge rather than nothing
            }
          }
          setDetailPanel({
            kind: "single-edge",
            edge: {
              from: detailEdge.getAttribute("data-edge-from") ?? "",
              to: detailEdge.getAttribute("data-edge-to") ?? "",
              kind: detailEdge.getAttribute("data-edge-kind") === "async" ? "async" : "sync",
              label: detailEdge.getAttribute("data-edge-label") ?? undefined,
              description: detailEdge.getAttribute("data-edge-description") ?? undefined,
              links,
            },
            ...anchor,
          });
        }
        return;
      }

      // Any other edge opens the focus canvas on it (#3031): its two cards and
      // every edge between them, labels in full. An aggregated edge whose
      // label is the panel's click target keeps the panel, wherever on the
      // edge the click lands.
      const focusEdge = target.closest(".krs-edge[data-edge-from][data-edge-to]");
      if (focusEdge && !focusEdge.querySelector("[data-domain-edges]")) {
        const opened = openFocus({
          kind: "edge",
          from: focusEdge.getAttribute("data-edge-from") ?? "",
          to: focusEdge.getAttribute("data-edge-to") ?? "",
        });
        if (opened) return;
      }

      // Check for info button click
      const infoButton = target.closest("[data-info-button]");
      if (infoButton) {
        const nodeId = infoButton.getAttribute("data-info-button");
        if (nodeId) {
          const nodeGroup = infoButton.closest("[data-node-id]") ?? infoButton;
          openDetailPanel(nodeId, nodeGroup);
        }
        return;
      }

      // Check for link button click
      const linkButton = target.closest("[data-link-button]");
      if (linkButton) {
        const nodeId = linkButton.getAttribute("data-link-button");
        if (nodeId) {
          const nodeGroup = linkButton.closest("[data-node-id]") ?? linkButton;
          openDetailPanel(nodeId, nodeGroup);
        }
        return;
      }

      // Check for deploy button click (system → deploy cross-navigation)
      const deployButton = target.closest("[data-deploy-button]");
      if (deployButton && onDeployButtonClick) {
        const serviceId = deployButton.getAttribute("data-deploy-button");
        if (serviceId) {
          onDeployButtonClick(serviceId);
          return;
        }
      }

      // Check for team button click (system → org cross-navigation)
      const teamButton = target.closest("[data-team-button]");
      if (teamButton && onTeamButtonClick) {
        const teamId = teamButton.getAttribute("data-team-button");
        if (teamId) {
          onTeamButtonClick(teamId);
          return;
        }
      }

      // Category collapse/expand control (Issue #1821): ⊖ on an open group or
      // ⊕ on a collapsed stub. The attribute carries the category to toggle.
      const categoryControl = target.closest("[data-collapse-category]");
      if (categoryControl && onCategoryToggle) {
        const category = categoryControl.getAttribute("data-collapse-category");
        if (category === "external" || category === "infra") {
          onCategoryToggle(category);
          return;
        }
      }

      // Group collapse/expand control (Issue #1858): ⊖/⊕ on a team boundary
      // frame. The attribute carries the team id to toggle.
      const groupControl = target.closest("[data-collapse-group]");
      if (groupControl && onGroupToggle) {
        const groupId = groupControl.getAttribute("data-collapse-group");
        if (groupId) {
          onGroupToggle(groupId);
          return;
        }
      }

      // In-place expansion control (Issue #1921): ⊕ on a collapsed service box
      // or ⊖ on an expanded container frame. The attribute carries the service
      // id to expand/collapse.
      const expandControl = target.closest("[data-expand-node]");
      if (expandControl && onExpandToggle) {
        const serviceId = expandControl.getAttribute("data-expand-node");
        if (serviceId) {
          onExpandToggle(serviceId);
          return;
        }
      }

      // Org Tree View team card (#2799): clicking it expands or collapses the
      // team's member grid. Deliberately ahead of the `[data-node-id]`
      // fallback below — the tree's team card carries both attributes
      // (`org-tree-renderer.ts`), and the toggle is what that click means.
      const teamCard = target.closest("[data-team-id]");
      if (teamCard && onTeamToggle) {
        const teamId = teamCard.getAttribute("data-team-id");
        if (teamId) {
          onTeamToggle(teamId);
          return;
        }
      }

      // Explicitly non-interactive elements (e.g. "+N more" overflow label)
      if (target.closest("[data-noop]")) return;

      // Check for owned service button click (org → system cross-navigation)
      const ownedServiceButton = target.closest("[data-owned-service-button]");
      if (ownedServiceButton && onOwnedServiceClick) {
        const serviceId = ownedServiceButton.getAttribute("data-owned-service-button");
        if (serviceId) {
          onOwnedServiceClick(serviceId);
          return;
        }
      }

      // Cmd/Ctrl+click jumps to the node's definition (ADR-177), drillable or
      // not, so it is checked before the click's other meanings. Every control
      // above is a button on the card, and keeps its own meaning.
      if ((e.metaKey || e.ctrlKey) && onJumpToEditor) {
        // A deploy card's `data-node-id` is its `<container>::<unit>` key, not
        // a node id; the container names the node it realizes (#2818). A
        // composite key with no realized node has nothing to jump to.
        const realizedId = target
          .closest("[data-realized-node-id]")
          ?.getAttribute("data-realized-node-id");
        const cardId = target.closest("[data-node-id]")?.getAttribute("data-node-id");
        const jumpNodeId = realizedId || (cardId && !cardId.includes("::") ? cardId : null);
        if (jumpNodeId) {
          onJumpToEditor(jumpNodeId);
          return;
        }
      }

      // Check for container click (deploy diagram cross-navigation)
      const containerGroup = target.closest("[data-container-id]");
      if (containerGroup && onContainerClick) {
        const containerId = containerGroup.getAttribute("data-container-id");
        // Synthetic deploy containers (`__unclassified__`, `__job_band__`) are
        // not real services — clicking them must not cross-navigate to a
        // non-existent system node.
        if (containerId && containerId !== "__unclassified__" && containerId !== "__job_band__") {
          // Hand over the realized node's id, not the container's: the
          // container id is its identity and may be qualified or quoted
          // (#2818), which the system view's `data-node-id` never is.
          onContainerClick(containerGroup.getAttribute("data-realized-node-id"));
          return;
        }
      }

      // Check for node click
      const nodeGroup = target.closest("[data-node-id]");
      if (!nodeGroup) {
        // Click outside any node: dismiss what the last interaction left open,
        // the detail panel and the cross-navigation highlight alike. A drag
        // never gets here (the threshold check above), so panning keeps both.
        setDetailPanel(null);
        onClearHighlight?.();
        return;
      }

      const hasChildren = nodeGroup.getAttribute("data-has-children") === "true";
      const nodeId = nodeGroup.getAttribute("data-node-id");
      const nodePath = nodeGroup.getAttribute("data-node-path");

      if (hasChildren && nodeId && onDrillDown) {
        // Drill down into child level. The card's `data-node-path` names the
        // one node it stands for (#2917); without it, use the viewPath the
        // bare-id index resolved, and last append the id to the current path.
        const drillPath =
          nodePath !== null
            ? parseNodePathRefId(nodePath)
            : (nodeMetadata.get(nodeId)?.viewPath ?? [...viewPath, nodeId]);
        setDetailPanel(null);
        onClearHighlight?.();
        onDrillDown(drillPath);
      } else if (nodeId) {
        // Open detail panel for leaf nodes
        onClearHighlight?.();
        openDetailPanel(nodeId, nodeGroup);
      }
    },
    [
      nodeMetadata,
      viewPath,
      onDrillDown,
      calcAnchor,
      openDetailPanel,
      openFocus,
      onContainerClick,
      onDeployButtonClick,
      onTeamButtonClick,
      onCategoryToggle,
      onGroupToggle,
      onExpandToggle,
      onOwnedServiceClick,
      onTeamToggle,
      onClearHighlight,
      onJumpToEditor,
    ],
  );

  const handleMouseLeave = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  // One `{ __html }` object per distinct `svg`, not one per render. React 19
  // compares `dangerouslySetInnerHTML` by object identity and, when it differs,
  // re-assigns `innerHTML` without comparing the string, so an inline literal
  // re-parses the diagram on every render. That would throw away the highlight
  // applied below while leaving its effect's dependencies unchanged, and the
  // class would not come back until the diagram itself changed (#2789).
  // Keeping this identity tied to `svg` is what makes the two move together.
  const svgHtml = useMemo(() => ({ __html: svg }), [svg]);

  // Apply highlight to the element that stands for the node after SVG injection
  useEffect(() => {
    if (!svgRef.current) return;

    // Clear previous highlights
    const prev = svgRef.current.querySelectorAll(".karasu-highlighted");
    prev.forEach((el) => el.classList.remove("karasu-highlighted"));

    if (!highlightedNodeId) return;

    // One attribute, chosen by the pane's view (#2818). No fallback to another
    // attribute: the id spaces differ, and a miss must stay a miss.
    const target = svgRef.current.querySelector(
      `[${highlightAttribute}="${CSS.escape(highlightedNodeId)}"]`,
    );
    if (target) target.classList.add("karasu-highlighted");
    // `svg` is a trigger, not a value this body reads: re-rendering the
    // diagram replaces the DOM nodes, so the highlight has to be re-applied
    // to the new ones. Dropping it would leave the class on a detached node.
    // eslint-disable-next-line react/exhaustive-effect-dependencies
  }, [highlightedNodeId, highlightAttribute, svg]);

  const closeFocus = useCallback(() => {
    setFocusTrail([]);
    setReturnToOutline(false);
  }, []);

  // The node panel's Relations button (#3057): the touch route to the focus
  // canvas. Counted the way the pill counts, so the number equals the lanes.
  const panelNodeId = detailPanel?.kind === "node" ? detailPanel.nodeId : null;
  const panelRelations = useMemo(
    () =>
      panelNodeId === null
        ? undefined
        : {
            count: relationsCount(readFocusSource(svg), panelNodeId),
            onOpen: () => {
              openFocus({ kind: "node", id: panelNodeId });
            },
          },
    [panelNodeId, svg, openFocus],
  );

  const nodePanelMetadata =
    detailPanel?.kind === "node"
      ? ((detailPanel.nodePath !== null
          ? nodeMetadataByPath.get(detailPanel.nodePath)
          : undefined) ?? nodeMetadata.get(detailPanel.nodeId))
      : undefined;

  return (
    <div
      className={`preview-pane${hasErrors ? " preview-pane--has-errors" : ""}${
        className ? ` ${className}` : ""
      }`}
    >
      <div
        ref={containerRef}
        className="preview-container"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onContextMenu={handleContextMenu}
        style={{ cursor: isDragging ? "grabbing" : "grab", position: "relative" }}
      >
        <div
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
            transformOrigin: "center center",
          }}
          ref={svgRef}
          dangerouslySetInnerHTML={svgHtml}
        />
        {edgeMenu && (
          <EdgeContextMenu
            x={edgeMenu.x}
            y={edgeMenu.y}
            canonicalId={edgeMenu.canonicalId}
            displayLabel={edgeMenu.displayLabel}
            edgeLabel={edgeMenu.edgeLabel}
            styleTargetPath={styleTargetPath}
            onPickDirection={handlePickDirection}
            onClose={() => setEdgeMenu(null)}
          />
        )}
        {detailPanel?.kind === "node" && nodePanelMetadata && (
          <NodeDetailPanel
            nodeId={detailPanel.nodeId}
            metadata={nodePanelMetadata}
            anchorX={detailPanel.anchorX}
            anchorY={detailPanel.anchorY}
            onClose={() => setDetailPanel(null)}
            onNavigateToDeploy={onDeployButtonClick}
            onNavigateToOrg={onTeamButtonClick}
            onJumpToEditor={onJumpToEditor ? () => onJumpToEditor(detailPanel.nodeId) : undefined}
            annotationDiff={
              (
                (detailPanel.nodePath !== null
                  ? nodeDiffByPath?.get(detailPanel.nodePath)
                  : undefined) ?? nodeDiff?.get(detailPanel.nodeId)
              )?.changes?.annotations
            }
            relations={panelRelations}
          />
        )}
        {detailPanel?.kind === "edge" && (
          <EdgeDetailPanel
            domainEdges={detailPanel.domainEdges}
            anchorX={detailPanel.anchorX}
            anchorY={detailPanel.anchorY}
            onClose={() => setDetailPanel(null)}
          />
        )}
        {focusShown && (
          <FocusCanvas
            source={focusSource}
            trail={focusTrail}
            onNavigate={(focus) => setFocusTrail((trail) => [...trail, focus])}
            onBack={() => setFocusTrail((trail) => trail.slice(0, -1))}
            onClose={closeFocus}
            returnFocus={
              returnToOutline
                ? () => document.querySelector<HTMLElement>('.outline-item[aria-current="true"]')
                : undefined
            }
          />
        )}
        {detailPanel?.kind === "single-edge" && (
          <EdgeDetailPanel
            edge={detailPanel.edge}
            anchorX={detailPanel.anchorX}
            anchorY={detailPanel.anchorY}
            onClose={() => setDetailPanel(null)}
          />
        )}
      </div>
      {hasErrors && svg && (
        <div className="error-state-overlay" aria-hidden="true">
          <span className="error-state-badge">⚠ Diagram is outdated — fix errors to update</span>
        </div>
      )}
      {visibleDiagnostics.length > 0 && (
        <div className="diagnostic-banner">
          {diagnosticBannerItems.map(({ d, message, location, key }) => (
            <div
              key={key}
              className={`diagnostic-banner__item diagnostic-banner__item--${d.severity}`}
            >
              {location ? `${location}: ${message}` : message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
